import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineInformationCircle } from "react-icons/hi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import {
  getLegalCertificateStampPaperFromVendorPreview,
  saveLegalCertificateStampPaperFromVendor,
  type LegalCertificateStampPaperFromVendorPreview,
} from "../../api/legalCertificate";

export const LEGAL_CERTIFICATE_STAMP_PAPER_FROM_VENDOR_FORM_ID =
  "legal-certificate-stamp-paper-from-vendor-form";

const LABEL_STYLE = "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const READONLY_BASE =
  "box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-[#EEEEEE] border border-[#E0E0E0] rounded-[6px] cursor-not-allowed text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_MUTED = "text-xs font-normal leading-5 text-[#6D6D6D]";

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function LegalCertificateStampPaperFromVendorView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);

  const [poaStamps, setPoaStamps] = useState(0);
  const [calculatedVendorAmount, setCalculatedVendorAmount] = useState(0);
  // Empty string = use calculated amount (override cleared).
  const [vendorAmountInput, setVendorAmountInput] = useState<string>("");
  const [vendorPaid, setVendorPaid] = useState<boolean | null>(null);

  const [calcTooltipOpen, setCalcTooltipOpen] = useState(false);
  const calcTooltipRef = useRef<HTMLDivElement>(null);

  const applyPreview = (data: LegalCertificateStampPaperFromVendorPreview) => {
    setPoaStamps(data.poaStamps ?? 0);
    setCalculatedVendorAmount(data.calculatedVendorAmount ?? 0);
    setVendorAmountInput(
      data.vendorAmountPayable != null ? String(data.vendorAmountPayable) : ""
    );
    setVendorPaid(data.vendorPaid ?? null);
  };

  useEffect(() => {
    if (!calcTooltipOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        calcTooltipRef.current &&
        !calcTooltipRef.current.contains(e.target as Node)
      ) {
        setCalcTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [calcTooltipOpen]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getLegalCertificateStampPaperFromVendorPreview(leadId)
      .then((data) => {
        if (cancelled) return;
        applyPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load stamp paper from vendor details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handlePaidChange = (value: string) => {
    if (value === "__none__") {
      setVendorPaid(null);
      return;
    }
    setVendorPaid(value === "yes");
  };

  const paidSelectValue =
    vendorPaid === true ? "yes" : vendorPaid === false ? "no" : "__none__";

  const validate = (): { ok: boolean; firstError?: string } => {
    const trimmed = vendorAmountInput.trim();
    if (trimmed !== "") {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return {
          ok: false,
          firstError: "Enter a valid Amount Payable to Stamp Vendor.",
        };
      }
    }
    if (vendorPaid === null) {
      return { ok: false, firstError: "Select Paid to Vendor (Yes or No)." };
    }
    return { ok: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    const validation = validate();
    if (!validation.ok) {
      if (validation.firstError) toast.error(validation.firstError);
      return;
    }
    setSaving(true);
    try {
      const trimmed = vendorAmountInput.trim();
      const data = await saveLegalCertificateStampPaperFromVendor(leadId, {
        vendorAmountPayable: trimmed === "" ? null : Number(trimmed),
        vendorPaid: vendorPaid ?? undefined,
      });
      applyPreview(data);
      toast.success("Stamp paper from vendor saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Loading stamp paper from vendor details...
        </p>
      </div>
    );
  }

  return (
    <form
      id={LEGAL_CERTIFICATE_STAMP_PAPER_FROM_VENDOR_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <h3 className="text-sm font-medium text-[#222222]">
        Stamp Paper from Vendor for Legal Certificate
      </h3>

      {/* Amount Payable to Stamp Vendor | Paid to Vendor */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <div className="flex items-center gap-1">
            <label
              className={LABEL_STYLE}
              style={{ ...LABEL_FEATURES, color: "#222222" }}
            >
              Amount Payable to Stamp Vendor
            </label>
            <div className="relative" ref={calcTooltipRef}>
              <button
                type="button"
                onClick={() => setCalcTooltipOpen((o) => !o)}
                className="flex items-center justify-center text-[#6D6D6D] hover:text-[#222222]"
                aria-label="Show calculation"
              >
                <HiOutlineInformationCircle
                  className="w-4 h-4"
                  strokeWidth={1.5}
                />
              </button>
              {calcTooltipOpen && (
                <div
                  className="absolute left-0 top-full mt-1 z-30 w-72 rounded-[8px] border border-[#EEEEEE] bg-white shadow-lg p-3 text-[12px] leading-5 text-[#222222]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <p className="text-xs font-semibold text-[#101111] mb-2">
                    Calculation Breakdown
                  </p>
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                      <span className="text-[#6D6D6D]">
                        PoA Stamps ({poaStamps} × Rs. 110)
                      </span>
                      <span>
                        Rs. {calculatedVendorAmount.toLocaleString("en-IN")}/-
                      </span>
                    </div>
                    <div className="border-t border-[#222] pt-1 mt-1 flex justify-between font-semibold text-[#101111]">
                      <span>Auto-calculated</span>
                      <span>
                        Rs. {calculatedVendorAmount.toLocaleString("en-IN")}/-
                      </span>
                    </div>
                    {vendorAmountInput.trim() !== "" &&
                      Number(vendorAmountInput) !== calculatedVendorAmount && (
                        <p className="mt-2 text-[11px] text-[#6D6D6D]">
                          Override in use. Clear the field to fall back to the
                          auto-calculated amount.
                        </p>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div
            className={INPUT_BASE}
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            <span className={`shrink-0 ${TEXT_MUTED}`}>Rs.</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={vendorAmountInput}
              onChange={(e) => setVendorAmountInput(e.target.value)}
              placeholder={
                calculatedVendorAmount > 0
                  ? String(calculatedVendorAmount)
                  : "Text Here"
              }
              className="flex-1 min-w-0 bg-transparent border-0 text-[12px] md:text-xs font-normal leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            />
          </div>
        </div>

        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#222222" }}
          >
            Paid to Vendor
          </label>
          <Select value={paidSelectValue} onValueChange={handlePaidChange}>
            <SelectTrigger className="h-9 min-h-[44px] md:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Select</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          {vendorPaid === false ? (
            <p
              className="text-[11px] font-medium leading-4 text-[#222222] mt-0.5"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
              role="status"
            >
              Status: Pending Payment
            </p>
          ) : vendorPaid === true ? (
            <p
              className="text-[11px] font-medium leading-4 text-[#222222] mt-0.5"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
              role="status"
            >
              Status: Paid
            </p>
          ) : null}
        </div>
      </div>

      {/* Number of Stamp Papers for PoA (read-only, sourced from Step 1) */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#6D6D6D" }}
          >
            Number of Stamp Papers for PoA
          </label>
          <div
            className={READONLY_BASE}
            aria-disabled="true"
            aria-readonly="true"
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            <span className="min-w-0 flex-1 truncate">
              {poaStamps > 0 ? String(poaStamps) : "—"}
            </span>
          </div>
        </div>
        {/* Spacer column to preserve two-column grid on md+. */}
        <div className="hidden md:block flex-1 min-w-0" aria-hidden />
      </div>
    </form>
  );
}
